import { Grid, Typography } from '@mui/material';
import {
  FunctionField,
  Labeled,
  Link,
  RaRecord,
  ReferenceField,
  Tab,
  TabProps,
  TextField,
  useRecordContext,
  useTranslate,
} from 'react-admin';

import { FormattedDateField } from 'components/FormattedDateField';
import { LiveHighlight } from 'components/LiveHighlight';
import SFIcon from 'components/SFIcon';
import { fontFamilyMonospace } from 'layout/theme';
import OnlineStatusField from '../../OnlineStatusField';
import { UpdaterStateSection } from './UpdaterStateSection';

const SoftwareVersionFields = () => {
  const record = useRecordContext();
  const softwareVersions = record?.software_versions || {};

  // Sort entries: tover_tafel first, tover_firmware second, then the rest
  const sortedEntries = Object.entries(softwareVersions).sort(([keyA], [keyB]) => {
    if (keyA === 'tover_tafel') return -1;
    if (keyB === 'tover_tafel') return 1;
    if (keyA === 'tover_firmware') return -1;
    if (keyB === 'tover_firmware') return 1;
    return 0;
  });

  return (
    <>
      {sortedEntries.map(([key, value]) => (
        <Grid item xs={1} key={key}>
          <LiveHighlight value={value} resetKey={record?.id}>
            <Labeled label={`resources.units.fields.software_versions.${key}`}>
              <FunctionField
                render={() => (
                  <span
                    style={{
                      fontWeight: !value ? 'bold' : 'normal',
                      color: !value ? '#d32f2f' : 'inherit',
                    }}
                  >
                    {String(value || 'Unknown')}
                  </span>
                )}
              />
            </Labeled>
          </LiveHighlight>
        </Grid>
      ))}
    </>
  );
};

export const InfoTab = (props: Omit<TabProps, 'children'>) => {
  const translate = useTranslate();
  const record = useRecordContext();
  return (
    <Tab {...props}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={10}>
          <Typography variant="h6" gutterBottom>
            {translate('resources.units.section.general')}
          </Typography>
          <Grid container item spacing={2} columns={{ xs: 1, sm: 4, md: 6 }}>
            <Grid item xs={1}>
              <Labeled>
                <TextField source="serial" fullWidth />
              </Labeled>
            </Grid>
            <Grid item xs={1}>
              <Labeled>
                <TextField source="configuration_key" emptyText="-" />
              </Labeled>
            </Grid>
            <Grid item xs={1}>
              <Labeled>
                <FunctionField
                  source="sf_id"
                  label="SalesForce ID"
                  render={(record: RaRecord) =>
                    record.sf_id ? (
                      <Link
                        //to={`${process.env.REACT_APP_SF}/sims/${record.mobile_network.sim_id}`}
                        to={`https://tover.lightning.force.com/lightning/r/Configuration__c/${record.sf_id}/view`}
                        target="_blank"
                      >
                        <SFIcon sx={{ position: 'relative', top: 8, marginRight: 1 }} />
                        {record.sf_id}
                      </Link>
                    ) : (
                      ''
                    )
                  }
                />
              </Labeled>
            </Grid>
            <Grid item xs={1}>
              <Labeled>
                <ReferenceField source="hardware_id" reference="hardware">
                  <TextField source="name" emptyText="-" />
                </ReferenceField>
              </Labeled>
            </Grid>
            <Grid item xs={1}>
              <Labeled>
                <ReferenceField source="hardware_revision_id" reference="hardware_revisions">
                  <TextField source="name" emptyText="-" />
                </ReferenceField>
              </Labeled>
            </Grid>
            <Grid item xs={1}>
              <Labeled>
                <TextField source="hostname" emptyText="-" />
              </Labeled>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {translate('resources.units.section.details')}
          </Typography>
          <Grid
            container
            item
            spacing={2}
            columns={{ xs: 1, sm: 4, md: Object.keys(record?.software_versions || {}).length + 2 }}
          >
            <Grid item xs={1}>
              <LiveHighlight value={record?.online_status} resetKey={record?.id}>
                <Labeled>
                  <OnlineStatusField source="online_status" />
                </Labeled>
              </LiveHighlight>
            </Grid>
            <Grid item xs={1}>
              {/* Last public (WAN) address the API saw, read-only — the API has no write path
                  for it. Rendered in monospace right next to the online status so it reads as
                  connection info. The helper line below the value is permanently visible on
                  purpose: it is the only thing that tells a keyboard or screen-reader operator
                  that this is not the VPN IP in the status bar above, which is a different
                  address and only exists while a tunnel is open. The value can be a 45-char
                  IPv6 string in a column that narrows as a unit reports more software
                  components, so it breaks anywhere. */}
              <Labeled>
                <TextField
                  source="real_ip"
                  emptyText="-"
                  sx={{ fontFamily: fontFamilyMonospace, wordBreak: 'break-all' }}
                />
              </Labeled>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {translate('resources.units.helpers.real_ip')}
              </Typography>
            </Grid>
            <SoftwareVersionFields />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <UpdaterStateSection />
        </Grid>

        <Grid item xs={12} lg={8}>
          <Typography variant="h6" gutterBottom>
            {translate('resources.units.section.history')}
          </Typography>
          <Grid container item spacing={2} columns={{ xs: 1, sm: 4, md: 6 }}>
            <Grid item xs={1}>
              <Labeled>
                <FormattedDateField source="install_date" />
              </Labeled>
            </Grid>
            <Grid item xs={1}>
              {/* Heartbeat-driven (about every 30 s while the page is open), so the quiet variant. */}
              <LiveHighlight value={record?.last_online} resetKey={record?.id} variant="pulse">
                <Labeled>
                  <FormattedDateField source="last_online" />
                </Labeled>
              </LiveHighlight>
            </Grid>
            <Grid item xs={1}>
              <Labeled>
                <FormattedDateField source="last_edit" />
              </Labeled>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Typography variant="h6" gutterBottom>
            {translate('resources.units.section.partner')}
          </Typography>
          <Grid container item spacing={2} columns={{ xs: 1, sm: 4, md: 6 }}>
            <Grid item xs={1}>
              <Labeled>
                <ReferenceField source="partner_id" reference="partners">
                  <TextField source="name" />
                </ReferenceField>
              </Labeled>
            </Grid>
            <Grid item xs={1}>
              <Labeled>
                <ReferenceField source="partner_group_id" reference="partner_groups" emptyText="-">
                  <TextField source="name" />
                </ReferenceField>
              </Labeled>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Tab>
  );
};
